import type { DraftSabotageEffect } from '@/hooks/useDraftSabotageEffects';

/**
 * SAB-931: which sabotage banners the draft room shows right now.
 *
 * ── WHY THIS EXISTS ─────────────────────────────────────────────────────────
 *
 * The V2 room had NO UI for five of the seven draft sabotage effects. Rule (e) meant it
 * never served a live draft, so nobody noticed. Three of the five are enforced server-side
 * (`timer_half` really does halve the clock, `force_auto` really does take the pick), so in
 * V2 they still HAPPEN -- the target simply gets no explanation for why their clock is
 * short or their pick vanished. The other two are the masking cards, handled separately.
 *
 * Ported from the legacy room's inline banners rather than redesigned: same effects, same
 * wording, same expiry arithmetic. A port that quietly changed what a paid card says is
 * worse than no port.
 *
 * ── LEAGUE-WIDE vs TARGETED, AND WHY THE ORDER IS FIXED ─────────────────────
 *
 * `reverse_order` and `league_guess_who` apply to everyone; the rest apply only when the
 * viewer is the target. Targeted banners come SECOND and are the more alarming ones -- a
 * manager needs "this is happening TO YOU" to be the last thing they read, not the first
 * thing scrolled past. The order is asserted, not incidental.
 *
 * ── PURE, SO THE MONEY-RELEVANT PART IS TESTABLE ────────────────────────────
 *
 * Sabotage cards are bought. "Is this banner shown to exactly the right viewer" is a claim
 * about a purchase working, so it lives in a function that can be tested against every
 * combination rather than inside JSX where only a rendered snapshot could reach it.
 */

export type SabotageBannerKind =
  | 'snake_reversal'
  | 'roughing'
  | 'fake_news'
  | 'delay_of_game'
  | 'blocked_punt';

export interface SabotageBanner {
  kind: SabotageBannerKind;
  emoji: string;
  title: string;
  /** Expiry countdown, or the fabricated injury line. Empty when there is nothing to add. */
  detail: string;
  /** 'league' = happening to everyone; 'attack' = happening to the viewer. */
  tone: 'league' | 'attack';
  /** Whether the banner should pulse. Reserved for the two that demand immediate attention. */
  urgent: boolean;
}

export interface DeriveBannersArgs {
  activeEffects: DraftSabotageEffect[] | null | undefined;
  /** The viewer's team. Null when spectating -- targeted banners then never show. */
  myTeamId: string | null | undefined;
  currentPickNumber: number;
  /** Teams in the league; used to convert an expiry ROUND into a pick count. */
  picksPerRound: number;
}

/**
 * "Ends in N picks", ported verbatim from the legacy room (SAB-450 item 4).
 *
 * Round expiry is converted to a pick count because a manager on the clock thinks in picks,
 * not rounds. `picksPerRound` of 0 would make the round branch produce nonsense, so it
 * falls through to the pick branch instead of dividing by a meaningless number.
 */
export function describeEffectExpiry(
  effect: Pick<DraftSabotageEffect, 'expires_at_round' | 'expires_at_pick'>,
  currentPickNumber: number,
  picksPerRound: number,
): string {
  if (effect.expires_at_round != null && picksPerRound > 0) {
    const lastPick = effect.expires_at_round * picksPerRound;
    const remaining = Math.max(0, lastPick - currentPickNumber + 1);
    if (remaining === 0) return 'Ending now';
    if (remaining === 1) return 'Ends after this pick';
    return `Ends in ${remaining} picks · after Round ${effect.expires_at_round}`;
  }
  if (effect.expires_at_pick != null) {
    const remaining = Math.max(0, effect.expires_at_pick - currentPickNumber + 1);
    if (remaining === 0) return 'Ending now';
    if (remaining === 1) return 'Ends after this pick';
    return `Ends in ${remaining} picks`;
  }
  return '';
}

export function deriveSabotageBanners(args: DeriveBannersArgs): SabotageBanner[] {
  const { myTeamId, currentPickNumber, picksPerRound } = args;
  // `active` is checked on every branch: an expired row lingers in the table, and rendering
  // a banner for a spent card would tell the target their punishment is still running.
  const effects = (args.activeEffects ?? []).filter(e => e.active);
  const expiry = (e: DraftSabotageEffect) => describeEffectExpiry(e, currentPickNumber, picksPerRound);

  const banners: SabotageBanner[] = [];

  // ── League-wide ───────────────────────────────────────────────────────────
  const reversal = effects.find(e => e.effect_type === 'reverse_order');
  if (reversal) {
    banners.push({
      kind: 'snake_reversal',
      emoji: '🐍',
      title: reversal.expires_at_round != null
        ? `SNAKE REVERSAL: Round ${reversal.expires_at_round} order is reversed`
        : 'SNAKE REVERSAL: the draft order is reversed',
      detail: expiry(reversal),
      tone: 'league',
      urgent: false,
    });
  }

  const roughing = effects.find(e => e.effect_type === 'league_guess_who');
  if (roughing) {
    banners.push({
      kind: 'roughing',
      emoji: '💥',
      title: 'ROUGHING THE DRAFTER: Everyone is in Guess Who mode this round',
      detail: expiry(roughing),
      tone: 'league',
      urgent: true,
    });
  }

  // ── Targeted: only when the viewer is the target ──────────────────────────
  // Without a team id there is no "you", so none of these may show. A spectator being told
  // their picks are being auto-drafted would be a fabrication.
  if (!myTeamId) return banners;
  const againstMe = (type: string) => effects.find(e => e.effect_type === type && e.target_team_id === myTeamId);

  const fakeNews = againstMe('fake_injury');
  if (fakeNews) {
    banners.push({
      kind: 'fake_news',
      emoji: '🚨',
      title: 'Breaking News',
      // The fabricated report IS the card. Falling back to generic copy keeps the banner
      // truthful about being an injury report even when effect_data is thin.
      detail: `${fakeNews.effect_data?.player_name || 'Star player'} ruled OUT: ${fakeNews.effect_data?.injury || 'undisclosed injury'}`,
      tone: 'attack',
      urgent: true,
    });
  }

  const delay = againstMe('timer_half');
  if (delay) {
    banners.push({
      kind: 'delay_of_game',
      emoji: '⏱️',
      title: 'DELAY OF GAME: your pick clock is halved',
      detail: expiry(delay),
      tone: 'attack',
      urgent: false,
    });
  }

  const punt = againstMe('force_auto');
  if (punt) {
    banners.push({
      kind: 'blocked_punt',
      emoji: '🦶',
      title: 'BLOCKED PUNT: your pick is being auto-drafted',
      detail: expiry(punt),
      tone: 'attack',
      urgent: true,
    });
  }

  return banners;
}
